import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SITE } from "@/lib/site";
import { pageSeo } from "@/lib/seo";
import {
  CompleteYourEstimateForm,
  useEstimateDetailsPageView,
} from "@/components/complete-your-estimate-form";
import {
  isEstimateCarryForward,
  readEstimateCarryForward,
  type EstimateCarryForward,
} from "@/lib/estimate-carry-forward";
import billGuide from "@/assets/estimate/electricity-bill-guide.png.asset.json";

export const Route = createFileRoute("/complete-your-estimate")({
  head: () => {
    const seo = pageSeo({
      path: "/complete-your-estimate",
      title: `Complete Your Estimate | ${SITE.name}`,
      description:
        "Add your address and optional electricity bill details so we can sharpen your personalised estimate.",
    });
    return {
      ...seo,
      meta: [...seo.meta, { name: "robots", content: "noindex, nofollow" }],
    };
  },
  component: CompleteYourEstimatePage,
});

function resolveCarryForward(locationState: unknown): EstimateCarryForward {
  if (isEstimateCarryForward(locationState)) {
    const hasAny = Boolean(
      locationState.name?.trim() ||
        locationState.email?.trim() ||
        locationState.phone?.trim() ||
        locationState.postcode?.trim() ||
        locationState.sourcePage?.trim(),
    );
    if (hasAny) return locationState;
  }
  return readEstimateCarryForward() ?? {};
}

function hasUsableCarriedContact(data: EstimateCarryForward): boolean {
  // Prior enquiry forms always collect name + phone; treat that as carried state.
  return Boolean(data.name?.trim() || data.phone?.trim());
}

function CompleteYourEstimatePage() {
  useEstimateDetailsPageView();

  const locationState = useRouterState({ select: (s) => s.location.state });
  const [carryForward, setCarryForward] = useState<EstimateCarryForward>({});

  useEffect(() => {
    setCarryForward(resolveCarryForward(locationState));
  }, [locationState]);

  const hasCarriedContact = hasUsableCarriedContact(carryForward);

  return (
    <section className="container mx-auto px-4 py-12 md:py-20">
      <div className="mx-auto max-w-5xl">
        <header className="mb-10 text-center md:text-left">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 text-balance">
            Thanks - We've Got Your Enquiry
          </h1>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Before we call, two quick things will make your estimate sharper: your address, and a
            recent electricity bill if you have one to hand. It takes about a minute - and if you
            can't find a bill, that's completely fine. We can estimate your usage and talk
            everything through on the call.
          </p>
        </header>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,640px)] lg:items-start">
          <div className="lg:sticky lg:top-6 order-last lg:order-first">
            <img
              src={billGuide.url}
              alt="What we need from your electricity bill - annual usage in kWh and unit rate in pence per kWh"
              className="w-full rounded-2xl border shadow-soft"
              loading="lazy"
            />
          </div>

          <div className="rounded-2xl border bg-card p-5 sm:p-6 md:p-8 shadow-soft">
            <CompleteYourEstimateForm
              key={`${carryForward.phone ?? ""}-${carryForward.email ?? ""}-${carryForward.postcode ?? ""}`}
              carryForward={carryForward}
              hasCarriedContact={hasCarriedContact}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
